import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";
import {
  ArrowLeft, Wrench, Camera, Download, Plus, Trash2, Calendar, User, Package, X, Loader2, AlertTriangle, Image as ImageIcon, MapPin,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const SEV_BADGE = {
  bassa: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  media: "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/40",
  alta: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
};
const STATO_BADGE = {
  aperto: "bg-[#FF3B30]/15 text-[#FF3B30]",
  in_lavorazione: "bg-[#FF9500]/15 text-[#FF9500]",
  risolto: "bg-[#39FF14]/15 text-[#39FF14]",
};
const STATO_LABEL = { aperto: "Aperto", in_lavorazione: "In lavorazione", risolto: "Risolto" };

export default function BikeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [bike, setBike] = useState(null);
  const [records, setRecords] = useState([]);
  const [reports, setReports] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [sedi, setSedi] = useState([]);
  const [geoSugg, setGeoSugg] = useState(null);
  const geoTried = useRef(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const qrRef = useRef(null);

  const loadBike = async () => {
    try {
      const b = await api.get(`/bikes/${id}`);
      setBike(b.data);
    } catch (e) {
      toast.error("Bici non trovata");
      navigate("/inventario");
    }
  };
  const loadRecords = async () => {
    const r = await api.get(`/maintenance`, { params: { bike_id: id } });
    setRecords(r.data);
  };
  const loadReports = async () => {
    const r = await api.get(`/reports`, { params: { bike_id: id } });
    setReports(r.data);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadBike();
      try { await loadRecords(); } catch (e) {}
      if (hasPerm("reports.view")) { try { await loadReports(); } catch (e) {} }
      if (hasPerm("warehouse.view")) { try { const p = await api.get("/parts"); setParts(p.data); } catch (e) {} }
      if (hasPerm("categories.view")) { try { const c = await api.get("/categories"); setCategories(c.data); } catch (e) {} }
      try { const sd = await api.get("/sedi"); setSedi(sd.data); } catch (e) {}
      try { const i = await api.get("/interventions"); setInterventions(i.data); } catch (e) {}
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [id]);

  // Rilevamento sede via geolocalizzazione dopo scansione QR (permesso negato = silenzioso, solo manuale)
  useEffect(() => {
    if (!bike || geoTried.current || !navigator.geolocation || !hasPerm("inventory.edit")) return;
    geoTried.current = true;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.post("/sedi/detect", { lat: pos.coords.latitude, lng: pos.coords.longitude });
          const s = r.data?.sede;
          if (s && s.id !== bike.sede_attuale) setGeoSugg(s);
        } catch (e) {}
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
    // eslint-disable-next-line
  }, [bike]);

  useEffect(() => {
    let revoked;
    (async () => {
      if (bike?.photo_path) {
        try {
          const res = await api.get(`/files/${bike.photo_path}`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data);
          revoked = url;
          setPhotoUrl(url);
        } catch (e) {}
      } else setPhotoUrl(null);
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [bike?.photo_path]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/bikes/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto caricata");
      await loadBike();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-bici-${bike.numero}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const delRecord = async (r) => {
    try { await api.delete(`/maintenance/${r.id}`); toast.success("Intervento eliminato"); await loadRecords(); await loadBike(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const setReportStato = async (rep, stato) => {
    try { await api.put(`/reports/${rep.id}`, { stato }); await loadReports(); toast.success("Stato aggiornato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const changeCategory = async (catId) => {
    const value = catId === "__none__" ? "" : catId;
    try {
      await api.put(`/bikes/${bike.id}`, {
        numero: bike.numero, modello: bike.modello || "", taglia: bike.taglia || "",
        numero_telaio: bike.numero_telaio || "", anno_produzione: bike.anno_produzione || "",
        categoria_noleggio: value, km_prossimo_tagliando: bike.km_prossimo_tagliando || 0, note: bike.note || "",
      });
      await loadBike();
      toast.success("Categoria aggiornata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const changeSede = async (sedeId, metodo = "manuale") => {
    const value = sedeId === "__none__" ? "" : sedeId;
    try {
      await api.put(`/bikes/${bike.id}/sede`, { sede_id: value, metodo });
      await loadBike();
      setGeoSugg(null);
      toast.success(metodo === "qr" ? "Spostamento registrato" : "Sede aggiornata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const sedeName = (sid) => sedi.find((s) => s.id === sid)?.nome || "—";

  const openReports = reports.filter((r) => r.stato !== "risolto");

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>;
  if (!bike) return null;

  const qrValue = `${window.location.origin}/b/${bike.numero}`;

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-5">
        <ArrowLeft className="h-4 w-4" /> Indietro
      </button>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 bg-[#f98125] rounded-sm flex items-center justify-center">
            <span className="font-display text-4xl text-white leading-none">{bike.numero}</span>
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">{bike.modello || "BICI " + bike.numero}</h1>
            <div className="text-zinc-500 text-sm mt-1">Taglia {bike.taglia || "—"} · Anno {bike.anno_produzione || "—"}</div>
          </div>
        </div>
        {(hasPerm("maintenance.create") || hasPerm("reports.create")) && (
          <div className="flex gap-2">
            {hasPerm("reports.create") && (
              <button onClick={() => navigate(`/segnalazioni?bike_id=${bike.id}`)} data-testid="report-damage-btn"
                className="flex items-center gap-2 rounded-sm bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#cc2f26] transition-colors active:scale-95">
                <AlertTriangle className="h-5 w-5" /> Segnala danno
              </button>
            )}
            {hasPerm("maintenance.create") && (
              <button onClick={() => setOpen(true)} data-testid="add-maintenance-btn"
                className="flex items-center gap-2 rounded-sm bg-[#39FF14] text-[#0A0A0A] font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#2dd60f] transition-colors active:scale-95">
                <Wrench className="h-5 w-5" /> Registra intervento
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left column: info + photo + qr */}
        <div className="space-y-5">
          {geoSugg && (
            <div data-testid="geofence-prompt" className="bg-[#f98125]/10 border border-[#f98125]/40 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-[#f98125] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Sei presso <b>{geoSugg.nome}</b></div>
                  <div className="text-xs text-zinc-400 mt-0.5">Vuoi spostare questa bici alla sede rilevata?</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => changeSede(geoSugg.id, "qr")} data-testid="geofence-confirm" className="rounded-sm bg-[#f98125] text-white font-bold text-xs px-3 py-2 hover:bg-[#d96a12]">Sposta a {geoSugg.nome}</button>
                    <button onClick={() => setGeoSugg(null)} data-testid="geofence-dismiss" className="rounded-sm border border-zinc-700 text-xs px-3 py-2 hover:bg-zinc-800">Ignora</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden">
            <div className="aspect-video bg-[#0A0A0A] relative flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={`Bici ${bike.numero}`} className="w-full h-full object-cover" />
              ) : (
                <div className="text-zinc-700 flex flex-col items-center gap-2"><Camera className="h-8 w-8" /><span className="text-xs">Nessuna foto</span></div>
              )}
              {hasPerm("inventory.edit") && (
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <button onClick={() => cameraRef.current?.click()} data-testid="capture-photo-btn"
                    className="flex items-center gap-1.5 bg-[#f98125] text-white text-xs px-3 py-2 rounded-sm hover:bg-[#d96a12] transition-colors">
                    <Camera className="h-4 w-4" /> Scatta foto
                  </button>
                  <button onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn"
                    className="flex items-center gap-1.5 bg-black/70 backdrop-blur text-white text-xs px-3 py-2 rounded-sm hover:bg-zinc-700 transition-colors">
                    <ImageIcon className="h-4 w-4" /> {photoUrl ? "Cambia" : "Galleria"}
                  </button>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} />
            </div>
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Dati tecnici</div>
            <Row label="Numero bici" value={bike.numero} />
            <Row label="Modello" value={bike.modello || "—"} />
            <Row label="Taglia" value={bike.taglia || "—"} />
            <Row label="Numero telaio" value={bike.numero_telaio || "—"} />
            <Row label="Anno produzione" value={bike.anno_produzione || "—"} />
            <Row label="Km totali noleggio" value={`${bike.km_totali_noleggio || 0} km`} />
            <Row label="Soglia tagliando" value={bike.km_prossimo_tagliando ? `${bike.km_prossimo_tagliando} km` : "—"} />
            {bike.a_noleggio && <div className="text-xs text-[#FF9500] flex items-center gap-1 pt-1"><AlertTriangle className="h-3 w-3" /> Attualmente fuori sede / a noleggio</div>}
            {bike.note && <Row label="Note" value={bike.note} />}
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-zinc-500">Categoria noleggio</span>
              {hasPerm("inventory.edit") && categories.length > 0 ? (
                <Select value={bike.categoria_noleggio || "__none__"} onValueChange={changeCategory}>
                  <SelectTrigger className="h-8 w-44 bg-[#0A0A0A] border-zinc-800 rounded-sm text-xs" data-testid="bike-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="__none__">Nessuna</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-white font-medium" data-testid="bike-category-value">{categories.find((c) => c.id === bike.categoria_noleggio)?.nome || "—"}</span>
              )}
            </div>
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-zinc-500">Sede attuale</span>
              {hasPerm("inventory.edit") ? (
                <Select value={bike.sede_attuale || "__none__"} onValueChange={(v) => changeSede(v, "manuale")}>
                  <SelectTrigger className="h-8 w-44 bg-[#0A0A0A] border-zinc-800 rounded-sm text-xs" data-testid="bike-sede-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="__none__">Nessuna sede</SelectItem>
                    {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-white font-medium" data-testid="bike-sede-value">{sedeName(bike.sede_attuale)}</span>
              )}
            </div>
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">QR Code</div>
            <div className="flex items-center gap-4">
              <div ref={qrRef} className="bg-white p-2 rounded-sm">
                <QRCodeCanvas value={qrValue} size={120} level="M" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-zinc-500 mb-3">Stampa e applica sulla bici. La scansione apre questa scheda.</p>
                <button onClick={downloadQR} data-testid="download-qr-btn"
                  className="flex items-center gap-2 text-sm bg-[#f98125] text-white px-4 py-2 rounded-sm hover:bg-[#d96a12] transition-colors">
                  <Download className="h-4 w-4" /> Scarica QR
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: reports + maintenance history */}
        <div className="lg:col-span-2 space-y-5">
          {hasPerm("reports.view") && openReports.length > 0 && (
            <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/40 rounded-sm p-5" data-testid="bike-open-reports">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-[#FF3B30]">
                  <AlertTriangle className="h-5 w-5" /> Segnalazioni aperte
                </h2>
                <span className="text-xs font-bold bg-[#FF3B30] text-white px-2 py-1 rounded-sm" data-testid="bike-open-reports-count">{openReports.length}</span>
              </div>
              <div className="space-y-3">
                {openReports.map((rep) => (
                  <div key={rep.id} data-testid={`bike-report-${rep.id}`} className="border border-zinc-800 rounded-sm p-4 bg-[#141414]">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${SEV_BADGE[rep.gravita]}`}>{rep.gravita}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATO_BADGE[rep.stato]}`}>{STATO_LABEL[rep.stato]}</span>
                    </div>
                    <p className="text-sm text-zinc-300">{rep.descrizione}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/60 gap-2">
                      <span className="text-xs text-zinc-500">Segnalato da {rep.created_by} · {fmtDate(rep.created_at)}</span>
                      {hasPerm("reports.edit") && (
                        <Select value={rep.stato} onValueChange={(v) => setReportStato(rep, v)}>
                          <SelectTrigger className="h-7 w-40 bg-[#0A0A0A] border-zinc-800 rounded-sm text-xs" data-testid={`bike-report-stato-${rep.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-zinc-800">
                            <SelectItem value="aperto">Aperto</SelectItem>
                            <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                            <SelectItem value="risolto">Risolto</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Wrench className="h-5 w-5 text-[#39FF14]" /> Storico manutenzioni</h2>
              <span className="text-xs text-zinc-500">{records.length} interventi</span>
            </div>

            {records.length === 0 ? (
              <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">
                Nessun intervento registrato per questa bici.
              </div>
            ) : (
              <div className="space-y-3">
                {records.map((r) => (
                  <div key={r.id} data-testid={`maintenance-record-${r.id}`} className="border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap gap-1.5">
                        {(r.intervention_names || []).map((n, i) => (
                          <span key={i} className="text-xs bg-[#f98125]/15 text-[#f98125] px-2 py-1 rounded-sm">{n}</span>
                        ))}
                      </div>
                      {hasPerm("maintenance.delete") && (
                        <button onClick={() => delRecord(r)} data-testid={`delete-maintenance-${r.id}`}
                          className="p-1 text-zinc-600 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                    {r.descrizione && <p className="text-sm text-zinc-300 mt-2">{r.descrizione}</p>}
                    {(r.parts_used || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.parts_used.map((p, i) => (
                          <span key={i} className="text-[11px] text-zinc-400 bg-zinc-800/60 px-2 py-0.5 rounded-sm flex items-center gap-1">
                            <Package className="h-3 w-3" /> {p.nome} ×{p.quantita}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-800/60 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(r.data)}</span>
                      <span className="flex items-center gap-1"><User className="h-3 w-3" /> {r.eseguito_da}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <MaintenanceDialog open={open} setOpen={setOpen} bike={bike} interventions={interventions}
        setInterventions={setInterventions} parts={parts} defaultExecutor={user?.name} hasPerm={hasPerm}
        onSaved={async () => { await loadRecords(); await loadBike(); }} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-white font-medium text-right">{value}</span>
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch (e) { return iso; }
}

function MaintenanceDialog({ open, setOpen, bike, interventions, setInterventions, parts, defaultExecutor, hasPerm, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [descrizione, setDescrizione] = useState("");
  const [eseguito, setEseguito] = useState(defaultExecutor || "");
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [usedParts, setUsedParts] = useState([]);
  const [newItv, setNewItv] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setSelected([]); setDescrizione(""); setEseguito(defaultExecutor || ""); setData(new Date().toISOString().slice(0, 10)); setUsedParts([]); } }, [open, defaultExecutor]);

  const toggle = (nome) => setSelected((s) => s.includes(nome) ? s.filter((x) => x !== nome) : [...s, nome]);

  const addCustom = async () => {
    if (!newItv.trim()) return;
    try {
      const res = await api.post("/interventions", { nome: newItv.trim() });
      setInterventions((arr) => [...arr, res.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setSelected((s) => [...s, res.data.nome]);
      setNewItv("");
      toast.success("Intervento aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const addPartRow = () => setUsedParts((p) => [...p, { part_id: "", nome: "", quantita: 1 }]);
  const setPartRow = (i, pid) => {
    const part = parts.find((x) => x.id === pid);
    setUsedParts((arr) => arr.map((r, idx) => idx === i ? { ...r, part_id: pid, nome: part?.nome || "" } : r));
  };
  const setQty = (i, q) => setUsedParts((arr) => arr.map((r, idx) => idx === i ? { ...r, quantita: parseInt(q) || 1 } : r));
  const removePartRow = (i) => setUsedParts((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (selected.length === 0 && !descrizione.trim()) { toast.error("Seleziona almeno un intervento o aggiungi una descrizione"); return; }
    if (!eseguito.trim()) { toast.error("Indica chi ha eseguito il lavoro"); return; }
    setSaving(true);
    try {
      await api.post("/maintenance", {
        bike_id: bike.id,
        intervention_names: selected,
        descrizione,
        eseguito_da: eseguito,
        data: new Date(data).toISOString(),
        parts_used: usedParts.filter((p) => p.part_id).map((p) => ({ part_id: p.part_id, nome: p.nome, quantita: p.quantita })),
      });
      toast.success("Intervento registrato");
      setOpen(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Wrench className="h-5 w-5 text-[#39FF14]" /> Registra intervento · Bici {bike.numero}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Interventi eseguiti</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {interventions.map((itv) => (
                <label key={itv.id} data-testid={`itv-option-${itv.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${selected.includes(itv.nome) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800 hover:border-zinc-700"}`}>
                  <Checkbox checked={selected.includes(itv.nome)} onCheckedChange={() => toggle(itv.nome)} />
                  <span className="text-sm">{itv.nome}</span>
                </label>
              ))}
            </div>
            {hasPerm("interventions.manage") && (
              <div className="flex gap-2 mt-3">
                <Input value={newItv} onChange={(e) => setNewItv(e.target.value)} data-testid="new-intervention-input"
                  placeholder="Aggiungi intervento personalizzato..." className="bg-[#0A0A0A] border-zinc-800 rounded-sm text-sm"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
                <button onClick={addCustom} data-testid="add-intervention-btn" className="flex items-center gap-1 px-3 rounded-sm border border-zinc-700 hover:bg-zinc-800 text-sm whitespace-nowrap"><Plus className="h-4 w-4" /> Aggiungi</button>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Descrizione / note</Label>
            <Textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} data-testid="maintenance-desc"
              className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} placeholder="Dettagli del lavoro svolto..." />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Eseguito da *</Label>
              <Input value={eseguito} onChange={(e) => setEseguito(e.target.value)} data-testid="maintenance-executor"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Data</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="maintenance-date"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
          </div>

          {hasPerm("warehouse.view") && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Ricambi utilizzati</Label>
                <button onClick={addPartRow} data-testid="add-part-row" className="text-xs text-[#f98125] flex items-center gap-1 hover:underline"><Plus className="h-3 w-3" /> Aggiungi ricambio</button>
              </div>
              <div className="space-y-2">
                {usedParts.map((row, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Select value={row.part_id} onValueChange={(v) => setPartRow(i, v)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm flex-1" data-testid={`part-select-${i}`}>
                        <SelectValue placeholder="Seleziona ricambio" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-zinc-800">
                        {parts.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.nome} (disp. {p.quantita})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="number" min="1" value={row.quantita} onChange={(e) => setQty(i, e.target.value)}
                      className="w-20 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid={`part-qty-${i}`} />
                    <button onClick={() => removePartRow(i)} className="p-2 text-zinc-500 hover:text-[#FF3B30]"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {usedParts.length === 0 && <p className="text-xs text-zinc-600">Nessun ricambio. Lo stock verrà scalato automaticamente.</p>}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="maintenance-save"
            className="rounded-sm bg-[#39FF14] text-[#0A0A0A] font-bold px-5 py-2.5 hover:bg-[#2dd60f] transition-colors disabled:opacity-50">
            {saving ? "Salvataggio..." : "Registra"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
